import { useEffect, useMemo, useRef, useState } from "react";

import { EarlyAccessFeature } from "@doitintl/cmp-models";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Box, Collapse, Divider } from "@mui/material";
import Button from "@mui/material/Button";
import capitalize from "lodash/capitalize";

import { awsRealTimeTxt } from "../../../../../assets/texts/Settings/aws";
import { useFeatureFlag } from "../../../../../Components/hooks/useFeatureFlag";
import useUnmountEffect from "../../../../../Components/hooks/useUnmountEffect";
import Icon from "../../../../../Components/Icons/Icon";
import { isLocalhost } from "../../../../../constants";
import { useAuthContext } from "../../../../../Context/AuthContext";
import { useCustomerContext } from "../../../../../Context/CustomerContext";
import mixpanel from "../../../../../utils/mixpanel";
import { addChannelsDoc } from "../../db";
import { AWSFeatureName, type CodeSnippet } from "../../types";
import { isBucketNameValid } from "../../utils";
import { UpdateErrorAlert } from "../Alerts/UpdateErrorAlert";
import { WaitingForUpdateAlert } from "../Alerts/WaitingForUpdateAlert";
import { CommandLineDialog } from "../Dialogs/CommandLineDialog";
import { StackCreationDialog } from "../Dialogs/StackCreationDialog";
import {
  getCLICreateCommand,
  getCLIUpdateCommand,
  getNotificationConfigurationSnippet,
  getQuickCreateUrl,
  getQuickUpdateUrl,
  getRealTimeDataCommand,
} from "./cloudFormationHelper";
import { FeaturesTable } from "./FeaturesTable";
import { listenForStackCreationUpdate } from "./listeners";

type FeaturesSelectionProps = {
  roleToUpdate?: string;
  accountSupportedFeatures?: string[];
  partiallyEnabledFeatures?: string[];
  onUpdate: () => void;
};

enum Mode {
  Selecting,
  WaitingForUpdate,
  UpdateFailed,
}

/**
 * Component to show AWS features selection, will be used for creating new account of updating existing
 * @param accountIdToUpdate - (optional) accountId to update. if missing the component will be used to add new account
 * @constructor
 */
export const FeaturesSelection = ({
  onUpdate,
  accountSupportedFeatures = [AWSFeatureName.core],
  roleToUpdate = "",
  partiallyEnabledFeatures = [],
}: FeaturesSelectionProps) => {
  const { customer } = useCustomerContext();
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [mode, setMode] = useState<Mode>(Mode.Selecting);
  const { currentUser } = useAuthContext({ mustHaveUser: true });
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");

  const [stackName, setStackName] = useState<string>("");
  const [cliCommands, setCliCommands] = useState<string[]>([]);
  const [codeSnippets, setCodeSnippets] = useState<CodeSnippet[]>([]);
  const [cliDialogOpen, setCliDialogOpen] = useState<boolean>(false);
  const [stackCreationDialogOpen, setStackCreationDialogOpen] = useState<boolean>(false);
  const [actionButtonText, setActionButtonText] = useState<string>(awsRealTimeTxt.LINK_ACCOUNT);
  const [operationTitle, setOperationTitle] = useState<string>(awsRealTimeTxt.LINKING);
  const isFSK8SFeatureEnabled = useFeatureFlag(EarlyAccessFeature.FSK8S);

  const [realTimeDataBucketName, setRealTimeDataBucketName] = useState<string>("");

  // will hold the unsubscribe function of snapshot that is used to detect status change in stack creation
  const unsubscribeListener = useRef<() => void>(() => {});

  const notificationConfigurationSnippet = useMemo(() => getNotificationConfigurationSnippet(isLocalhost), []);

  useEffect(() => {
    if (roleToUpdate) {
      setActionButtonText(awsRealTimeTxt.UPDATE_ACCOUNT);
      setOperationTitle(awsRealTimeTxt.UPDATING);
    }

    if (isBucketNameValid(realTimeDataBucketName)) {
      setCodeSnippets([notificationConfigurationSnippet]);
    } else {
      setCodeSnippets([]);
    }
  }, [roleToUpdate, realTimeDataBucketName, notificationConfigurationSnippet]);

  // Memoize CLI commands based on bucket name and dialog state
  const displayedCliCommands = useMemo(() => {
    if (!cliDialogOpen) {
      return cliCommands;
    }

    const updatedCommands = [...cliCommands];

    // Update the bucket name in the stack command
    if (updatedCommands[0]) {
      updatedCommands[0] = updatedCommands[0].replace(
        /(ParameterKey=RealTimeDataBucketName,ParameterValue=)[^\s]*/,
        `$1${realTimeDataBucketName}`
      );
    }

    // Update real-time data command if bucket name is provided and valid
    if (realTimeDataBucketName && isBucketNameValid(realTimeDataBucketName)) {
      const realTimeDataCommand = getRealTimeDataCommand(realTimeDataBucketName);
      if (updatedCommands.length === 1) {
        updatedCommands.push(realTimeDataCommand);
      } else {
        updatedCommands[1] = realTimeDataCommand;
      }
    } else if (updatedCommands.length > 1) {
      updatedCommands.pop(); // Remove the real-time data command if bucket name is invalid
    }

    return updatedCommands;
  }, [cliDialogOpen, cliCommands, realTimeDataBucketName]);

  useUnmountEffect(() => {
    unsubscribeListener.current();
  });

  const disableToLinkAccount = useMemo(() => {
    if (roleToUpdate) {
      return selectedFeatures.length === 0 || mode !== Mode.Selecting;
    }

    return false;
  }, [roleToUpdate, selectedFeatures, mode]);

  // start listen for status change for specific stack
  const startListeningForChanges = (stackName: string) => {
    const onChangeDetectedCallback = (error: string | undefined) => {
      unsubscribeListener.current();
      if (error) {
        setErrorMessage(error);
        setMode(Mode.UpdateFailed);
      } else {
        onUpdate();
      }
    };

    unsubscribeListener.current();
    addChannelsDoc(customer.id, currentUser.uid, stackName);
    setMode(Mode.WaitingForUpdate);
    unsubscribeListener.current = listenForStackCreationUpdate(
      customer.id,
      currentUser.uid,
      stackName,
      onChangeDetectedCallback
    );
  };

  const navigateToAwsQuickLinkPage = () => {
    setAlertMessage(awsRealTimeTxt.AWS_CLOUD_FORMATION_STACK_CREATION_WAITING_TXT);
    mixpanel.track("aws.link-account");
    if (selectedFeatures.includes(AWSFeatureName.spotScaling)) {
      mixpanel.track("spot-scaling.link-account");
    }
    if (selectedFeatures.includes(AWSFeatureName.quotas)) {
      mixpanel.track("aws.link-account.quotas");
    }
    if (selectedFeatures.includes(AWSFeatureName.realTimeData)) {
      mixpanel.track("aws.link-account.real-time-data");
    }
    const [url, stackName] = roleToUpdate
      ? getQuickUpdateUrl(customer.id, roleToUpdate, selectedFeatures, isFSK8SFeatureEnabled, realTimeDataBucketName)
      : getQuickCreateUrl(customer.id, selectedFeatures, isFSK8SFeatureEnabled, realTimeDataBucketName);

    startListeningForChanges(stackName);

    window.open(url, "_blank");

    setStackCreationDialogOpen(false);
  };

  const onOpenCLIDialogClicked = () => {
    const [stackCommand, stackName] = roleToUpdate
      ? getCLIUpdateCommand(customer.id, roleToUpdate, selectedFeatures, isFSK8SFeatureEnabled, realTimeDataBucketName)
      : getCLICreateCommand(customer.id, selectedFeatures, isFSK8SFeatureEnabled, realTimeDataBucketName);

    const realTimeDataCommand = getRealTimeDataCommand(realTimeDataBucketName);
    const commands = [stackCommand];

    if (realTimeDataBucketName !== "") {
      commands.push(realTimeDataCommand);
    }

    setStackName(stackName);
    setCliCommands(commands);
    setCliDialogOpen(true);
    setAlertMessage(awsRealTimeTxt.AWS_CLI_WAITING_TXT);
  };

  const onCloseAlertClicked = () => {
    unsubscribeListener.current();
    setMode(Mode.Selecting);
  };

  const isRealTimeSelected = selectedFeatures.includes(AWSFeatureName.realTimeData);

  return (
    <>
      <StackCreationDialog
        open={stackCreationDialogOpen}
        setOpen={setStackCreationDialogOpen}
        operationName={actionButtonText}
        linkAccountHandler={navigateToAwsQuickLinkPage}
        showBucketNameInput={isRealTimeSelected}
        bucketName={realTimeDataBucketName}
        onBucketNameChange={setRealTimeDataBucketName}
      />
      <CommandLineDialog
        open={cliDialogOpen}
        onCopy={() => {
          startListeningForChanges(stackName);
        }}
        setOpen={setCliDialogOpen}
        cliCommands={displayedCliCommands}
        codeSnippets={codeSnippets}
        showBucketNameInput={isRealTimeSelected}
        bucketName={realTimeDataBucketName}
        onBucketNameChange={setRealTimeDataBucketName}
      />
      <FeaturesTable
        accountSupportedFeatures={accountSupportedFeatures}
        disabled={mode !== Mode.Selecting}
        onSelectionChanged={setSelectedFeatures}
        realTimeDataBucketName={realTimeDataBucketName}
        onRealTimeDataBucketNameChanged={setRealTimeDataBucketName}
        partiallyEnabledFeatures={partiallyEnabledFeatures}
      />
      <Box
        sx={{
          pt: 2,
        }}
      >
        <Collapse in={mode === Mode.WaitingForUpdate}>
          <WaitingForUpdateAlert operationTitle={operationTitle} message={alertMessage} onClose={onCloseAlertClicked} />
        </Collapse>
        <Collapse in={mode === Mode.UpdateFailed}>
          <UpdateErrorAlert error={errorMessage} onClose={onCloseAlertClicked} />
        </Collapse>
      </Box>
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          height: "60px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          px: 4,
          bgcolor: "background.paper",
          zIndex: 100,
        }}
      >
        <Divider sx={{ position: "absolute", top: 0, left: 0, right: 0 }} />
        <Box
          sx={{
            width: "100%",
            maxWidth: 1100,
            display: "flex",
            justifyContent: "flex-start",
            gap: 1,
            flexDirection: "row-reverse",
          }}
        >
          <Button
            startIcon={<OpenInNewIcon />}
            color="primary"
            variant="contained"
            onClick={() => {
              setStackCreationDialogOpen(true);
            }}
            disabled={disableToLinkAccount}
            data-cy="aws-features-link-account"
          >
            {capitalize(actionButtonText)}
          </Button>
          <Button
            startIcon={<Icon icon="cli" size={20} />}
            color="primary"
            variant="outlined"
            disabled={disableToLinkAccount}
            onClick={onOpenCLIDialogClicked}
            data-cy="aws-features-cli-button"
          >
            {awsRealTimeTxt.PREFER_CLI}
          </Button>
        </Box>
      </Box>
    </>
  );
};
